import { Flex, Tooltip } from 'antd';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';

import type { GetFinancialStatisticsResponse } from '@app/api/Analytics';

interface UseCalendarHeaderStatsModelArgs {
  financialStatistics?: GetFinancialStatisticsResponse;
  dateString: string;
}

interface StatisticsItem {
  key: string;
  label: string;
  value: string;
}

type StatisticsCounterTone = 'success' | 'danger';

interface StatisticsCounter {
  key: string;
  label: string;
  value: string;
  tone: StatisticsCounterTone;
}

interface StatisticsSummary {
  dateLabel: string;
  counters: StatisticsCounter[];
}

type PublishedNumberValue = BigNumber.Value | null | undefined;

interface FormatPublishedNumberOptions {
  suffix?: string;
  fallbackValue?: string;
}

const RU_GROUP_SEPARATOR = '\u00A0';
const RU_DECIMAL_SEPARATOR = ',';

const normalizeStringNumber = (value: string): string => {
  return value.trim().replace(/\s/g, '').replace(',', '.');
};

const toBigNumberOrNull = (value: PublishedNumberValue): BigNumber | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const bigNumberValue = BigNumber.isBigNumber(value)
    ? value
    : new BigNumber(
        typeof value === 'string' ? normalizeStringNumber(value) : value
      );

  if (!bigNumberValue.isFinite()) {
    return null;
  }

  return bigNumberValue;
};

const formatBigNumberRu = (value: BigNumber): string => {
  const roundedValue = value.isInteger()
    ? value
    : value.decimalPlaces(2, BigNumber.ROUND_HALF_UP);

  const sign = roundedValue.isNegative() && !roundedValue.isZero() ? '-' : '';
  const [integerPart, fractionPart] = roundedValue.abs().toFixed().split('.');

  const groupedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    RU_GROUP_SEPARATOR
  );

  if (!fractionPart) {
    return `${sign}${groupedIntegerPart}`;
  }

  return `${sign}${groupedIntegerPart}${RU_DECIMAL_SEPARATOR}${fractionPart}`;
};

const formatPublishedNumber = (
  value: PublishedNumberValue,
  options: FormatPublishedNumberOptions = {}
): string => {
  const { suffix, fallbackValue = '0' } = options;

  const bigNumberValue = toBigNumberOrNull(value);

  if (!bigNumberValue) {
    return fallbackValue;
  }

  const formattedValue = formatBigNumberRu(bigNumberValue);

  return suffix ? `${formattedValue} ${suffix}` : formattedValue;
};

const formatOptionalPublishedNumber = (
  value: PublishedNumberValue,
  suffix?: string
): string | null => {
  const bigNumberValue = toBigNumberOrNull(value);

  if (!bigNumberValue) {
    return null;
  }

  const formattedValue = formatBigNumberRu(bigNumberValue);

  return suffix ? `${formattedValue} ${suffix}` : formattedValue;
};

const formatCountValue = (value: PublishedNumberValue): string => {
  return formatPublishedNumber(value);
};

const formatMoneyValue = (value: PublishedNumberValue): string => {
  return formatPublishedNumber(value, {
    suffix: '',
  });
};

const formatOptionalMoneyValue = (
  value: PublishedNumberValue
): string | null => {
  return formatOptionalPublishedNumber(value, '');
};

const formatOptionalPercentValue = (
  value: PublishedNumberValue
): string | null => {
  return formatOptionalPublishedNumber(value, '%');
};

const formatPlanMoneyValue = (value: PublishedNumberValue): string => {
  return formatOptionalMoneyValue(value) ?? '—';
};

const formatPlanPercentValue = (value: PublishedNumberValue): string => {
  return formatOptionalPercentValue(value) ?? '—';
};

export const useCalendarHeaderStatsModel = ({
  financialStatistics,
  dateString,
}: UseCalendarHeaderStatsModelArgs) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    SalesPlanToday = null,
    PlanCompletionToday = null,
    AmountTotal = null,
    AmountCash = null,
    AmountBank = null,
    AdditionalPaymentAmount = null,
    RecordsAllAmount = null,
    RecordsPaidAmount = null,
    AmountNoVisited = null,
    CountClients = null,
    CountClientNoVisited = null,
  } = financialStatistics || {};

  const summary = useMemo<StatisticsSummary>(
    () => ({
      dateLabel: moment(dateString).locale('ru').format('D MMMM'),
      counters: [
        {
          key: 'clients',
          label: 'Клиентов',
          value: formatCountValue(CountClients),
          tone: 'success',
        },
        {
          key: 'cancellations',
          label: 'Отмен',
          value: formatCountValue(CountClientNoVisited),
          tone: 'danger',
        },
      ],
    }),
    [CountClientNoVisited, CountClients, dateString]
  );

  const detailRows = useMemo<StatisticsItem[]>(() => {
    const rows = [
      {
        key: 'sales-plan-today',
        label: 'План продаж',
        value: SalesPlanToday,
        formatter: formatPlanMoneyValue,
      },
      {
        key: 'plan-completion-today',
        label: 'Выполнение плана',
        value: PlanCompletionToday,
        formatter: formatPlanPercentValue,
      },
      {
        key: 'amount-total',
        label: 'Поступлений в кассы',
        value: AmountTotal,
        formatter: formatMoneyValue,
      },
      {
        key: 'amount-cash',
        label: 'Оплата наличными',
        value: AmountCash,
        formatter: formatMoneyValue,
      },
      {
        key: 'amount-bank',
        label: 'Оплата безналом',
        value: AmountBank,
        formatter: formatMoneyValue,
      },
      {
        key: 'records-all-amount',
        label: 'Записей на сумму',
        value: RecordsAllAmount,
        formatter: formatMoneyValue,
      },
      {
        key: 'records-paid-amount',
        label: 'Выполнено на сумму',
        value: RecordsPaidAmount,
        formatter: formatMoneyValue,
      },
      {
        key: 'additional-payment-amount',
        label: 'Допродаж на сумму',
        value: AdditionalPaymentAmount,
        formatter: formatMoneyValue,
      },
      {
        key: 'amount-no-visited',
        label: 'Отменено на сумму',
        value: AmountNoVisited,
        formatter: formatMoneyValue,
      },
    ] satisfies Array<{
      key: string;
      label: string;
      value: PublishedNumberValue;
      formatter(value: PublishedNumberValue): string;
    }>;

    return rows.map(({ key, label, value, formatter }) => ({
      key,
      label,
      value: formatter(value),
    }));
  }, [
    SalesPlanToday,
    PlanCompletionToday,
    AmountTotal,
    AmountCash,
    AmountBank,
    RecordsAllAmount,
    RecordsPaidAmount,
    AdditionalPaymentAmount,
    AmountNoVisited,
  ]);

  const triggerText = useMemo(() => {
    const salesPlanValue = formatOptionalMoneyValue(SalesPlanToday);
    const planCompletionValue = formatOptionalPercentValue(PlanCompletionToday);
    const amountTotalValue = formatOptionalMoneyValue(AmountTotal);

    if (!salesPlanValue || !planCompletionValue) {
      return [
        'План не установлен',
        amountTotalValue ? `Итого: ${amountTotalValue}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
    }

    return (
      <Flex align="center" gap={4}>
        <Tooltip title={'Выполнено на сумму'}>
          <span style={{ fontWeight: 'bold' }}>{RecordsPaidAmount ?? '0'}</span>
        </Tooltip>
        <span>Выполнено: {planCompletionValue}</span>
      </Flex>
    );
  }, [AmountTotal, PlanCompletionToday, RecordsPaidAmount, SalesPlanToday]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setIsOpen(nextOpen);
  }, []);

  return {
    isOpen,
    triggerText,
    summary,
    detailRows,
    handleOpenChange,
  };
};
